import Pagination from "./Pagination";
import { TFormValues, TTableColumn } from "../../types/common";
import {
  CTable,
  CTableBody,
  CTableDataCell,
  CTableFoot,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from "@coreui/react";
import { TableFilter } from "./TableFilter";
import styled from "styled-components";
import { beautify, getStatusColor, numberFormat } from "../../utils/helpers";
import { Select, Tag } from "antd";
import { useState } from "react";
import { blueColor } from "../styled/Theme";
import { TableSkeleton } from "./Loader";

type TTableProps = {
  loading: boolean;
  count: number;
  tableTitle?: string;
  tableFooter?: any;
  tableTotals?: Record<string, any>;
  tableColumns: TTableColumn[];
  tableData: any[];
  hideFilters?: boolean;
  filters?: any;
  onFilterChange?: any;
  updateFormValues?(obj: TFormValues): void;
  tableStyle?: any;
  status?: any;
  noEmptyText?: boolean;
  showPagination?: boolean;
  tableHeaderItem?: any;
  pageItemsSize?: number;
};

export const Table = (props: TTableProps) => {
  const {
    tableData,
    tableColumns,
    tableFooter,
    tableTotals,
    tableTitle,
    loading,
    count,
    filters,
    hideFilters,
    tableStyle,
    onFilterChange,
    status,
    noEmptyText = true,
    showPagination = true,
    tableHeaderItem,
    pageItemsSize = 10,
  } = props;

  const [pageSize, setPageSize] = useState(filters?.page_size || pageItemsSize);

  const onChange = (key: string, value: string | number) => {
    const updatedFilters = { ...filters };
    delete updatedFilters.page;
    if (value === "" || value === null || value === undefined) {
      delete updatedFilters[key];
      if (onFilterChange) onFilterChange(updatedFilters);
      return;
    } else {
      if (onFilterChange) onFilterChange({ ...updatedFilters, [key]: value });
    }
  };

  function getCount() {
    if (count > 1000) return `${(count / 1000).toFixed(0)}K`;
    return count;
  }

  const startValue =
    ((filters?.page || 1) - 1) * (filters?.page_size || pageSize) + 1;
  const endValue =
    ((filters?.page || 1) - 1) * (filters?.page_size || pageSize) +
    tableData?.length;

  const counter_text = (
    <>
      Showing of{" "}
      <b>
        {numberFormat(startValue)} - {numberFormat(endValue)}
      </b>{" "}
      of {getCount() || 0} items
    </>
  );

  const PerPageDropdown = () => (
    <div className="flexRow gap10 alignCenter">
      <span style={{ fontSize: 12, color: "#64748b", whiteSpace: "nowrap" }}>
        Items per page
      </span>
      <Select
        options={[
          { label: "10", value: 10 },
          { label: "20", value: 20 },
          { label: "30", value: 30 },
          { label: "50", value: 50 },
          { label: "75", value: 75 },
          { label: "100", value: 100 },
        ]}
        onChange={(value) => {
          setPageSize(value);
          onFilterChange({ ...filters, page: 1, page_size: value });
        }}
        value={filters?.page_size || pageSize}
        style={{ width: 72 }}
        size="small"
      />
    </div>
  );

  return (
    <TableWrapper>
      {/* ── Header ── */}
      <TitleWrapper>
        <TitleLeft>
          <TitleText>
            <span className="capitalize">{tableTitle}</span>
            {status && (
              <Tag className="capitalize" color={getStatusColor(status)}>
                {beautify(status)}
              </Tag>
            )}
          </TitleText>
          {count > 0 && <CounterPill>{counter_text}</CounterPill>}
        </TitleLeft>

        <TitleRight>
          {count > 0 && <PerPageDropdown />}
          {tableHeaderItem && (
            <span style={{ fontSize: 12 }}>{tableHeaderItem}</span>
          )}
        </TitleRight>
      </TitleWrapper>

      {/* ── Table ── */}
      <div
        className="table-wrapper"
        style={{
          overflowX: "auto",
          overflowY: "auto",
          maxHeight: "75vh",
          ...tableStyle,
        }}
      >
        <StyledTable striped>
          <CTableHead>
            {/* Column labels */}
            <CTableRow>
              <TH $first>#</TH>
              {tableColumns?.map((item, i) => (
                <TH key={i} className={item?.noWrap ? "no-wrap" : ""}>
                  <span className="capitalize">{item.header}</span>
                </TH>
              ))}
            </CTableRow>

            {/* Filters */}
            {!hideFilters && (
              <CTableRow>
                <FilterTH />
                {tableColumns?.map((row, i) =>
                  row.filter ? (
                    <FilterTH key={i}>
                      <TableFilter
                        name={row.filter?.key ? row.filter?.key : row.id}
                        onChange={onChange}
                        updateFormValues={onFilterChange}
                        updateFilterValues={onFilterChange}
                        filters={filters}
                        selectOptions={row.filter?.options}
                        {...row.filter}
                      />
                    </FilterTH>
                  ) : (
                    <FilterTH key={i} />
                  ),
                )}
              </CTableRow>
            )}
          </CTableHead>

          <CTableBody>
            {loading ? (
              <CTableRow>
                <CTableHeaderCell
                  scope="row"
                  colSpan={tableColumns?.length + 1}
                  style={{ padding: 0, border: "none", fontWeight: "normal" }}
                >
                  <TableSkeleton
                    rows={pageSize}
                    columnHints={tableColumns?.map((c: any) => ({
                      narrow: c.narrow,
                      wide: c.wide,
                    }))}
                  />
                </CTableHeaderCell>
              </CTableRow>
            ) : tableData?.length ? (
              tableData.map((item, idx) => (
                <TR key={idx}>
                  <IndexTD scope="row">
                    {item?.index || startValue + idx}
                  </IndexTD>
                  {tableColumns.map((column) => (
                    <TD
                      key={column.id}
                      className={column?.noWrap ? "no-wrap" : ""}
                    >
                      {column.render
                        ? column.render(item, item)
                        : item[column.id] ||
                          (noEmptyText && <EmptyDash>—</EmptyDash>)}
                    </TD>
                  ))}
                </TR>
              ))
            ) : (
              <CTableRow>
                <CTableHeaderCell
                  scope="row"
                  colSpan={tableColumns?.length + 1}
                  className="text-center"
                  style={{
                    padding: "36px 0",
                    fontWeight: "normal",
                    fontSize: 12,
                    color: "#94a3b8",
                  }}
                >
                  No data available
                </CTableHeaderCell>
              </CTableRow>
            )}
          </CTableBody>

          {/* Totals */}
          {tableTotals && Object.keys(tableTotals).length > 0 && (
            <Totalsfoot>
              <CTableRow>
                <TotalsTH>Totals</TotalsTH>
                {tableColumns?.map((column) => (
                  <TotalsTH
                    key={column.id}
                    className={column?.noWrap ? "no-wrap" : ""}
                  >
                    <span className="capitalize bold">
                      {tableTotals[column.id]}
                    </span>
                  </TotalsTH>
                ))}
              </CTableRow>
            </Totalsfoot>
          )}

          {/* Custom footer */}
          {tableFooter && (
            <CTableFoot>
              <CTableRow>
                <TotalsTH>#</TotalsTH>
                {tableColumns?.map((column, i) => (
                  <TotalsTH key={i} className={column?.noWrap ? "no-wrap" : ""}>
                    <span className="capitalize">
                      {tableFooter?.[column.id]}
                    </span>
                  </TotalsTH>
                ))}
              </CTableRow>
            </CTableFoot>
          )}
        </StyledTable>
      </div>

      <div className="kv-panel-after" />

      {/* ── Pagination ── */}
      {showPagination && tableData?.length > 0 && (
        <Pagination
          count={count}
          filters={filters}
          onFilterChange={onFilterChange}
          loading={loading}
          defaultPageSize={pageItemsSize}
          showPageSizeSelector={false}
        />
      )}
    </TableWrapper>
  );
};

/* ─── Styled components ─────────────────────────────────────────── */

const TableWrapper = styled.div`
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  background: #fff;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);

  /* no-wrap utility */
  .no-wrap {
    white-space: nowrap;
  }
`;

/* Header */
const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  border-bottom: 1px solid #e8f0f8;
  background: #fff;
  gap: 12px;
  flex-wrap: wrap;
`;

const TitleLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const TitleRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const TitleText = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
  letter-spacing: -0.1px;
`;

const CounterPill = styled.div`
  font-size: 11.5px;
  color: #64748b;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  padding: 2px 10px;
  white-space: nowrap;
`;

/* Table */
// const StyledTable = styled(CTable)`
//   && {
//     margin: 0;
//     border-collapse: collapse;
//     width: 100%;

//     /* Alternating rows */
//     tbody tr:nth-of-type(odd) {
//       background: #fafbfd;
//     }
//     tbody tr:nth-of-type(even) {
//       background: #ffffff;
//     }
//   }
// `;
const StyledTable = styled(CTable)`
  && {
    margin: 0;
    border-collapse: separate;
    border-spacing: 0;
    width: 100%;

    /* Sticky header */
    thead {
      position: sticky;
      top: 0;
      z-index: 2;
    }

    /* Alternating rows */
    tbody tr:nth-of-type(odd) {
      background: #fafbfd;
    }
    tbody tr:nth-of-type(even) {
      background: #ffffff;
    }
  }
`;

/* Column header cells */
// const TH = styled(CTableHeaderCell)<{ $first?: boolean }>`
//   && {
//     font-size: 11px;
//     font-weight: 600;
//     text-transform: uppercase;
//     letter-spacing: 0.5px;
//     color: #64748b;
//     background: #f8fafc;
//     border-top: none;
//     border-bottom: 1.5px solid #e2e8f0;
//     border-right: 1px solid #e9edf3;
//     border-left: none;
//     padding: 8px 10px;
//     white-space: nowrap;
//     vertical-align: middle;

//     &:last-child {
//       border-right: none;
//     }
//   }
// `;
const TH = styled(CTableHeaderCell)<{ $first?: boolean }>`
  && {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #64748b;
    background: #f8fafc;
    border-top: none;
    border-bottom: none;
    border-right: 1px solid #e9edf3;
    border-left: none;
    padding: 8px 10px;
    white-space: nowrap;
    vertical-align: middle;
    /* sticky-safe bottom border */
    box-shadow: inset 0 -1.5px 0 #e2e8f0;

    &:last-child {
      border-right: none;
    }
  }
`;

/* Filter row cells */
const FilterTH = styled(CTableHeaderCell)`
  && {
    background: #f8fafc;
    border-bottom: 1px solid #e2e8f0;
    border-right: 1px solid #e9edf3;
    border-top: none;
    border-left: none;
    padding: 5px 6px;
    vertical-align: middle;

    &:last-child {
      border-right: none;
    }

    input.form-control {
      height: 28px;
      font-size: 11.5px;
      padding: 3px 8px;
      border: 1px solid #e2e8f0;
      border-radius: 6px;
      color: #334155;
      background: #fff;
      transition: border-color 0.18s;
      width: 100%;

      &:focus {
        outline: none;
        border-color: ${blueColor};
        box-shadow: 0 0 0 2px ${blueColor}18;
      }
      &::placeholder {
        color: #cbd5e1;
      }
    }

    .ant-select .ant-select-selector {
      height: 28px !important;
      font-size: 11.5px !important;
      border-radius: 6px !important;
      border-color: #e2e8f0 !important;
      padding: 0 8px !important;
    }
    .ant-select-selection-item {
      line-height: 26px !important;
      font-size: 11.5px !important;
    }

    .ant-picker {
      height: 28px !important;
      font-size: 11.5px !important;
      border-radius: 6px !important;
      border-color: #e2e8f0 !important;
      padding: 0 8px !important;
    }
  }
`;

/* Data rows */
const TR = styled(CTableRow)`
  && {
    border-bottom: 1px solid #edf1f7;
    transition: background 0.12s;

    &:hover {
      background: #eff6ff !important;
    }
    &:last-child {
      border-bottom: none;
    }
  }
`;

const IndexTD = styled(CTableHeaderCell)`
  && {
    font-size: 11px;
    font-weight: 550;
    color: #94a3b8;
    padding: 7px 10px;
    border: none;
    border-right: 1px solid #e9edf3;
    vertical-align: middle;
    width: 36px;
  }
`;

const TD = styled(CTableDataCell)`
  && {
    font-size: 12px;
    color: #334155;
    padding: 7px 10px;
    vertical-align: middle;
    border: none;
    border-right: 1px solid #e9edf3;

    &:last-child {
      border-right: none;
    }

    /* Links */
    a {
      color: ${blueColor};
      font-weight: 500;
      text-decoration: none;
      &:hover {
        text-decoration: underline;
      }
    }

    /* Ant tags inside cells */
    .ant-tag {
      font-size: 11px;
      border-radius: 20px;
      padding: 1px 8px;
      font-weight: 600;
      border: none;
      line-height: 18px;
    }
  }
`;

const EmptyDash = styled.span`
  color: #c8d3de;
  font-size: 12px;
`;

/* Totals footer */
const Totalsfoot = styled(CTableFoot)`
  && {
    background: #f8fafc;
    border-top: 1.5px solid #e2e8f0;
  }
`;

const TotalsTH = styled(CTableHeaderCell)`
  && {
    font-size: 11.5px;
    font-weight: 700;
    color: #334155;
    padding: 7px 10px;
    background: #f8fafc;
    border: none;
    border-right: 1px solid #e9edf3;
    &:last-child {
      border-right: none;
    }
  }
`;

// import ReactPaginate from "react-paginate";
// import { TFormValues, TTableColumn } from "../../types/common";
// import {
//   CTable,
//   CTableBody,
//   CTableDataCell,
//   CTableFoot,
//   CTableHead,
//   CTableHeaderCell,
//   CTableRow,
// } from "@coreui/react";
// import { TableFilter } from "./TableFilter";
// import styled from "styled-components";
// import { beautify, getStatusColor, numberFormat } from "../../utils/helpers";
// import { Select, Tag } from "antd";
// import { EmptyWrapper } from "./Elements";
// import Loader from "./Loader";
// import { useState } from "react";

// type TTableProps = {
//   loading: boolean;
//   count: number;
//   tableTitle?: string;
//   tableFooter?: any;
//   tableTotals?: Record<string, any>;
//   tableColumns: TTableColumn[];
//   tableData: any[];
//   hideFilters?: boolean;
//   // filters?: TFilter;
//   // onFilterChange?(prop: TFilter): void;
//   filters?: any;
//   onFilterChange?: any;
//   updateFormValues?(obj: TFormValues): void;
//   tableStyle?: any;
//   status?: any;
//   noEmptyText?: boolean;
//   showPagination?: boolean;
//   tableHeaderItem?: any;
//   pageItemsSize?: number;
// };

// export const Table = (props: TTableProps) => {
//   const {
//     tableData,
//     tableColumns,
//     tableFooter,
//     tableTotals,
//     tableTitle,
//     loading,
//     count,
//     filters,
//     hideFilters,
//     tableStyle,
//     onFilterChange,
//     status,
//     noEmptyText = true,
//     showPagination = true,
//     tableHeaderItem,
//     pageItemsSize = 10,
//   } = props;

//   const [pageSize, setPageSize] = useState(filters?.page_size || pageItemsSize);

//   const onChange = (key: string, value: string | number) => {
//     const updatedFilters = { ...filters };
//     delete updatedFilters.page;
//     if (value === "" || value === null || value === undefined) {
//       delete updatedFilters[key];
//       if (onFilterChange) onFilterChange(updatedFilters);
//       return;
//     } else {
//       if (onFilterChange) onFilterChange({ ...updatedFilters, [key]: value });
//     }
//   };

//   function getCount() {
//     if (count > 1000) return `${(count / 1000).toFixed(0)}K`;
//     return count;
//   }

//   // paginated table records
//   const startValue =
//     ((filters?.page || 1) - 1) * (filters?.page_size || pageSize) + 1;
//   const endValue =
//     ((filters?.page || 1) - 1) * (filters?.page_size || pageSize) +
//     tableData?.length;
//   const counter_text = (
//     <>
//       Showing of{" "}
//       <b>
//         {numberFormat(startValue)} - {numberFormat(endValue)}
//       </b>{" "}
//       of {getCount() || 0} items
//     </>
//   );
//   const PerPageDropdown = () => {
//     return (
//       <div className="flexRow gap10 alignCenter">
//         <span className="font13">Items per page</span>
//         <Select
//           options={[
//             { label: "10", value: 10 },
//             { label: "20", value: 20 },
//             { label: "30", value: 30 },
//             { label: "50", value: 50 },
//             { label: "75", value: 75 },
//             { label: "100", value: 100 },
//           ]}
//           onChange={(value) => {
//             setPageSize(value);
//             onFilterChange({ ...filters, page: 1, page_size: value });
//           }}
//           value={filters?.page_size || pageSize}
//           style={{ width: 100 }}
//         />
//       </div>
//     );
//   };

//   return (
//     <TableWrapper className="">
//       <TitleWrapper className="whiteBg flexCenter spaceCenter">
//         <div className="flex gap10">
//           <span className="capitalize bold">{tableTitle}</span>
//           {status && (
//             <Tag className="capitalize" color={getStatusColor(status)}>
//               {beautify(status)}
//             </Tag>
//           )}
//         </div>
//         {/* COUNTER TEXT */}
//         <FlexWrapper>
//           {count > 0 && (
//             <div className="flexRow gap10 alignCenter">
//               <PerPageDropdown />
//               <span className="font13">{counter_text}</span>
//             </div>
//           )}
//           {tableHeaderItem && <span className="font13">{tableHeaderItem}</span>}
//         </FlexWrapper>
//       </TitleWrapper>

//       <div className="table-wrapper" style={{ ...tableStyle }}>
//         <div className="responsive-table">
//           <CTable style={{ ...tableStyle }} striped>
//             <CTableHead>
//               <CTableRow>
//                 <CTableHeaderCell style={{ fontSize: "85%" }} scope="col">
//                   #
//                 </CTableHeaderCell>
//                 {tableColumns?.map((item) => (
//                   <CTableHeaderCell
//                     className={item?.noWrap ? "no-wrap" : ""}
//                     style={{ fontSize: "85%" }}
//                     scope="col"
//                   >
//                     <span className="capitalize">{item.header}</span>
//                   </CTableHeaderCell>
//                 ))}
//               </CTableRow>

//               {hideFilters ? null : (
//                 <CTableRow>
//                   <CTableHeaderCell
//                     style={{ fontSize: "85%" }}
//                     scope="col"
//                   ></CTableHeaderCell>

//                   {tableColumns?.map((row, i) => {
//                     return row.filter ? (
//                       <CTableHeaderCell
//                         key={i}
//                         style={{
//                           whiteSpace: "nowrap",
//                           border: "1px solid #ddd",
//                         }}
//                       >
//                         <TableFilter
//                           name={row.filter?.key ? row.filter?.key : row.id}
//                           onChange={onChange}
//                           updateFormValues={onFilterChange}
//                           updateFilterValues={onFilterChange}
//                           filters={filters}
//                           selectOptions={row.filter?.options}
//                           {...row.filter}
//                         />
//                       </CTableHeaderCell>
//                     ) : (
//                       <CTableHeaderCell />
//                     );
//                   })}
//                 </CTableRow>
//               )}
//             </CTableHead>
//             <CTableBody>
//               {loading ? (
//                 <CTableRow>
//                   <CTableHeaderCell
//                     scope="row"
//                     colSpan={tableColumns?.length + 1}
//                     className="text-center"
//                     style={{ whiteSpace: "nowrap", fontWeight: "normal" }}
//                   >
//                     <Loader />
//                   </CTableHeaderCell>
//                 </CTableRow>
//               ) : tableData?.length ? (
//                 tableData.map((item, idx) => (
//                   <CTableRow>
//                     <CTableHeaderCell style={{ fontSize: "85%" }} scope="row">
//                       {item?.index || startValue + idx}
//                     </CTableHeaderCell>
//                     {tableColumns.map((column) => (
//                       <CTableDataCell
//                         key={column.id}
//                         style={{ fontSize: "85%" }}
//                         className={column?.noWrap ? "no-wrap" : ""}
//                       >
//                         {column.render
//                           ? column.render(item, item)
//                           : item[column.id] ||
//                             (noEmptyText && <EmptyWrapper>Empty</EmptyWrapper>)}
//                       </CTableDataCell>
//                     ))}
//                   </CTableRow>
//                 ))
//               ) : (
//                 <CTableRow>
//                   <CTableHeaderCell
//                     scope="row"
//                     colSpan={tableColumns?.length + 1}
//                     className="text-center font14"
//                     style={{ whiteSpace: "nowrap", fontWeight: "normal" }}
//                   >
//                     No data available
//                   </CTableHeaderCell>
//                 </CTableRow>
//               )}
//             </CTableBody>
//             {tableTotals && Object.keys(tableTotals).length > 0 && (
//               <CTableFoot>
//                 <CTableRow>
//                   <CTableHeaderCell style={{ fontSize: "85%" }} scope="col">
//                     Totals
//                   </CTableHeaderCell>
//                   {tableColumns?.map((column) => (
//                     <CTableHeaderCell
//                       key={column.id}
//                       className={column?.noWrap ? "no-wrap" : ""}
//                       style={{ fontSize: "85%" }}
//                       scope="col"
//                     >
//                       <span className="capitalize bold">
//                         {tableTotals[column.id]}
//                       </span>
//                     </CTableHeaderCell>
//                   ))}
//                 </CTableRow>
//               </CTableFoot>
//             )}
//             {tableFooter && (
//               <CTableFoot>
//                 <CTableRow>
//                   <CTableHeaderCell style={{ fontSize: "85%" }} scope="col">
//                     #
//                   </CTableHeaderCell>
//                   {tableColumns?.map((column) => (
//                     <CTableHeaderCell
//                       className={column?.noWrap ? "no-wrap" : ""}
//                       style={{ fontSize: "85%" }}
//                       scope="col"
//                     >
//                       <span className="capitalize">
//                         {tableFooter?.[column.id]}
//                       </span>
//                     </CTableHeaderCell>
//                   ))}
//                 </CTableRow>
//               </CTableFoot>
//             )}
//           </CTable>
//         </div>
//       </div>

//       <div className="kv-panel-after" />
//       <div className="panel-footer">
//         {showPagination && (
//           <div className="kv-panel-pager">
//             {loading ||
//               (tableData?.length > 0 && count ? (
//                 <ReactPaginate
//                   breakLabel="..."
//                   previousLabel={<span>«</span>}
//                   onPageChange={({ selected }) =>
//                     onFilterChange({ ...filters, page: selected + 1 })
//                   }
//                   pageRangeDisplayed={3}
//                   pageCount={Math.ceil(
//                     count / (filters?.page_size || pageSize),
//                   )}
//                   marginPagesDisplayed={3}
//                   nextLabel={<span>»</span>}
//                   renderOnZeroPageCount={null}
//                   containerClassName="pagination justify-content-end"
//                   pageClassName="page-item"
//                   pageLinkClassName="page-link"
//                   previousClassName="page-item"
//                   previousLinkClassName="page-link"
//                   nextClassName="page-item"
//                   nextLinkClassName="page-link"
//                   activeClassName="active"
//                   forcePage={(filters?.page || 1) - 1}
//                   breakClassName="page-item"
//                   breakLinkClassName="page-link"
//                 />
//               ) : null)}
//           </div>
//         )}
//         <div className="clearfix" />
//       </div>
//     </TableWrapper>
//   );
// };

// const TableWrapper = styled.div`
//   border-radius: 10px;
//   overflow: hidden;
//   border: 1px solid #ddd;

//   .panel-footer {
//     padding: 0 20px;
//   }
// `;

// const TitleWrapper = styled.div`
//   padding: 10px 20px;
//   border-bottom: 1px solid #ddd;
// `;

// const FlexWrapper = styled.div`
//   display: flex;
//   flex-direction: row;
//   gap: 1rem;
//   align-items: center;
// `;
